'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { Customer, Loyalty, editCustomer, getCustomerDetail, getLoyalty } from '@/apis/tds';

interface CustomerDetailContextProps {
  customer: Customer | null;
  loyalty: Loyalty | null;
  loading: boolean;
  editPersonalInformationRequest: (data: { [key: string]: any }) => Promise<boolean | null>;
}

const CustomerDetailContext = createContext<CustomerDetailContextProps | undefined>(undefined);

interface CustomerDetailProviderProps {
  children: ReactNode;
}

export const hasSessionCookies = (): boolean => {
  return document.cookie.includes('AUTH_SESSION');
}
export const clearSessionCookies = () => {
  document.cookie = 'AUTH_SESSION=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' +
            process.env.NEXT_PUBLIC_TRAILWAYS_COOKIE_DOMAIN + ';';
}

export const CustomerDetailProvider: React.FC<CustomerDetailProviderProps> = ({ children }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loyalty, setLoyalty] = useState<Loyalty | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const run = async () => {
      try {
        if (hasSessionCookies()) {
          const [customerResponse, loyaltyResponse] = await Promise.all([getCustomerDetail(), getLoyalty()]);

          if (customerResponse && customerResponse.customerId) {
            setCustomer(customerResponse);
          }

          if (loyaltyResponse) {
            setLoyalty(loyaltyResponse);
          }
        }
        return;
      } catch (error) {
        setCustomer(null);
        setLoyalty(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const editPersonalInformationRequest = async (data: { [key: string]: any }) => {
    const response = await editCustomer(data);
    if (response.statusCode && response.statusCode !== 200) {
      return false;
    }
    setCustomer(response);
    return true;
  };

  return (
    <CustomerDetailContext.Provider value={{ customer, loyalty, loading, editPersonalInformationRequest }}>
      {children}
    </CustomerDetailContext.Provider>
  );
};

export const useCustomerDetail = (): CustomerDetailContextProps => {
  const context = useContext(CustomerDetailContext);
  return (
    context || {
      customer: null,
      loyalty: null,
      loading: true,
      editPersonalInformationRequest: async (data: { [key: string]: any }): Promise<boolean | null> => null
    }
  );
};
